// UsageProgress Component
// Displays credit usage with a progress bar and statistics

import { FiTrendingUp, FiAlertCircle } from 'react-icons/fi';
import styles from './UsageProgress.module.css';

import { logger } from '../../../utils/logger';

interface UsageProgressProps {
  current: number;
  limit: number;
  label?: string;
  showPercentage?: boolean;
  showWarning?: boolean;
}

export function UsageProgress({
  current,
  limit,
  label = 'Credits Used',
  showPercentage = true,
  showWarning = true,
}: UsageProgressProps) {
  const percentage = limit > 0 ? (current / limit) * 100 : 0;
  const remaining = Math.max(0, limit - current);
  const isOverLimit = current > limit;

  logger.log('📊 UsageProgress:', {
    current,
    limit,
    remaining,
    percentage: percentage.toFixed(2) + '%',
    isOverLimit
  });

  // Determine color based on usage
  const getColor = () => {
    if (isOverLimit || percentage >= 100) return 'var(--color-error)';
    if (percentage >= 90) return 'var(--color-error)';
    if (percentage >= 70) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getVariant = () => {
    if (isOverLimit || percentage >= 100) return 'danger';
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  const isLow = percentage >= 80;
  const isCritical = percentage >= 90 || isOverLimit;

  return (
    <div className={styles.usageProgress}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.labelRow}>
          <span className={styles.label}>{label}</span>
          {showWarning && isLow && (
            <div className={`${styles.warning} ${isCritical ? styles.critical : ''}`}>
              <FiAlertCircle />
              <span>{isCritical ? 'Critical' : 'Low'}</span>
            </div>
          )}
        </div>
        {showPercentage && (
          <span className={styles.percentage} style={{ color: getColor() }}>
            {percentage.toFixed(1)}%
          </span>
        )}
      </div>

      {/* Progress Bar */}
      <div className={styles.progressBar}>
        <div
          className={`${styles.progressFill} ${styles[getVariant()]}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        >
          <div className={styles.progressGlow} />
        </div>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <FiTrendingUp className={styles.statIcon} />
          <span className={styles.statLabel}>Used:</span>
          <span className={styles.statValue}>{current.toLocaleString()}</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Remaining:</span>
          <span className={styles.statValue} style={{ color: getColor() }}>
            {remaining.toLocaleString()}
          </span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Limit:</span>
          <span className={styles.statValue}>{limit.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

